import { ref, computed, onMounted } from 'vue'
import { useFlagsWithLocalStorage } from '@acepoker/games-nlhe/frontend/helpers/featureToggles/useFlagsWithLocalStorage'
import type { FeatureFlagName } from '@/lib/flags'
export interface FlagOptions {
  loadImmediately?: boolean // Whether to load flags on mount
  logDebug?: boolean // Whether to log debug info
  defaultValue?: boolean // Default value if flag doesn't exist
}
const defaultOptions: FlagOptions = {
  loadImmediately: true,
  logDebug: false,
  defaultValue: false
}

export type FlagsReturnType = ReturnType<typeof useFlags>
/**
 * A composable for using feature flags in components
 * @param flagNames - Array of flag names to load
 * @param options - Configuration options
 * @returns Object with flags and helper methods
 */
export function useFlags(flagNames: FeatureFlagName[], options: FlagOptions = {}) {
  const mergedOptions = { ...defaultOptions, ...options }
  // State
  const isLoading = ref(true)
  const error = ref<Error | null>(null)
  const flags = ref<Record<string, { enabled: boolean; value: unknown }>>({})
  // Load flags function
  const loadFlags = async () => {
    isLoading.value = true
    error.value = null
    try {
      flags.value = await useFlagsWithLocalStorage(flagNames)
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Failed to load feature flags')
    } finally {
      isLoading.value = false
    }
  }
  // Helper function to check if a flag is enabled
  const isEnabled = (flagName: FeatureFlagName) => {
    return computed(() => {
      // Don't use flag value if still loading or there was an error
      if (isLoading.value) return mergedOptions.defaultValue
      return flags.value[flagName]?.enabled ?? mergedOptions.defaultValue
    })
  }
  // Get the flag value
  const getValue = (flagName: FeatureFlagName, defaultVal: unknown = null) => {
    return computed(() => {
      if (isLoading.value) return defaultVal
      return flags.value[flagName]?.value ?? defaultVal
    })
  }
  // Load flags on mount if configured to do so
  if (mergedOptions.loadImmediately) {
    onMounted(loadFlags)
  }
  return {
    flags,
    isLoading,
    error,
    loadFlags,
    isEnabled,
    getValue
  }
}
